// ─── Token Store ────────────────────────────────────────────────────────────
// • accessToken & idToken — MEMORY ONLY. Short-lived; lost on page reload,
//   which is fine — they are rebuilt by the next refresh call.
// • refreshToken — read straight from the `refreshToken` cookie set by the
//   backend / OAuth layer. JS does not write or delete it under normal
//   operation; the backend manages its lifecycle via Set-Cookie. Reading
//   the cookie means the session survives reload, tab duplication and a
//   fresh tab on the same origin (anywhere the cookie is sent).

const REFRESH_TOKEN_COOKIE = "refreshToken";

const readRefreshFromCookie = (): string | null => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === REFRESH_TOKEN_COOKIE) {
      return decodeURIComponent(value);
    }
  }
  return null;
};

/**
 * Best-effort clear of the refresh cookie on logout — covers all plausible
 * (path, domain) combos since we can't be sure how the backend originally
 * set it. Note: an httpOnly refresh cookie cannot be deleted from JS; the
 * backend should also clear it on its logout endpoint.
 */
const clearRefreshCookie = () => {
  const hostname = window.location.hostname;
  const hostParts = hostname.split(".");
  const domains: string[] = [""];
  for (let i = 0; i < hostParts.length; i++) {
    const d = hostParts.slice(i).join(".");
    if (d) {
      domains.push(d);
      domains.push("." + d);
    }
  }
  const paths = ["/", window.location.pathname, ""];
  paths.forEach((path) => {
    domains.forEach((domain) => {
      const pathAttr = path ? `; path=${path}` : "";
      const domainAttr = domain ? `; domain=${domain}` : "";
      document.cookie = `${REFRESH_TOKEN_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathAttr}${domainAttr}`;
      document.cookie = `${REFRESH_TOKEN_COOKIE}=; Max-Age=0${pathAttr}${domainAttr}`;
    });
  });
};

let accessToken: string | null = null;
let idToken: string | null = null;

interface Tokens {
  accessToken?: string | null;
  idToken?: string | null;
  // refreshToken accepted for API symmetry but ignored — the backend owns
  // the cookie's lifecycle.
  refreshToken?: string | null;
}

export const tokenStore = {
  /** Store all three tokens at once — used right after login/refresh. */
  setTokens: (tokens: Tokens) => {
    if (tokens.accessToken !== undefined) accessToken = tokens.accessToken;
    if (tokens.idToken !== undefined) idToken = tokens.idToken;
    // refreshToken intentionally not stored — it lives in the cookie.
  },

  setAccessToken: (token: string | null) => {
    accessToken = token;
  },

  /** No-op: refresh token lives in a backend-managed cookie, not JS state. */
  setRefreshToken: (_token: string | null) => {
    // Intentionally empty.
  },

  getAccessToken: (): string | null => accessToken,
  getIdToken: (): string | null => idToken,
  getRefreshToken: (): string | null => readRefreshFromCookie(),

  /** Wipe everything — call on logout. */
  clearTokens: () => {
    accessToken = null;
    idToken = null;
    clearRefreshCookie();
  },
};
